/**
 * UpdateDatabaseConnectionModal Component
 *
 * Modal for updating an existing database connection.
 * Branches form by connectionType: self-managed shows full form, managed shows simplified form.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { FormField } from '../../../components/forms/FormField';
import { useUpdateDatabaseConnection } from '../hooks/useUpdateDatabaseConnection';
import { z } from 'zod';
import type { DatabaseConnection, DatabaseEngine } from '../../../types/database-connection.types';

// Self-managed form validation schema (password optional for updates)
const selfManagedFormSchema = z.object({
  dbConnectionName: z
    .string()
    .min(1, 'Connection name is required')
    .max(100, 'Connection name must not exceed 100 characters'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  engine: z.enum(['SQL_SERVER', 'MYSQL', 'POSTGRESQL', 'ORACLE'], {
    required_error: 'Database engine is required',
  }),
  host: z.string().min(1, 'Host is required').max(255, 'Host must not exceed 255 characters'),
  port: z
    .string()
    .min(1, 'Port is required')
    .refine((val) => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 1 && num <= 65535;
    }, 'Port must be between 1 and 65535'),
  database: z.string().min(1, 'Database name is required').max(128, 'Database name must not exceed 128 characters'),
  username: z.string().min(1, 'Username is required').max(128, 'Username must not exceed 128 characters'),
  password: z.string().max(1024, 'Password must not exceed 1024 characters').optional(),
  ssl: z.boolean().optional(),
  isActive: z.boolean().optional(),
  portForwarding: z.boolean().optional(),
  sshHost: z.string().max(255, 'SSH host must not exceed 255 characters').optional(),
  sshPort: z
    .string()
    .optional()
    .refine((val) => {
      if (!val || val === '') return true;
      const num = parseInt(val);
      return !isNaN(num) && num >= 1 && num <= 65535;
    }, 'SSH port must be between 1 and 65535'),
  sshUsername: z.string().max(128, 'SSH username must not exceed 128 characters').optional(),
});

// Managed form validation schema (only name, description, isActive editable)
const managedFormSchema = z.object({
  dbConnectionName: z
    .string()
    .min(1, 'Connection name is required')
    .max(100, 'Connection name must not exceed 100 characters'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  isActive: z.boolean().optional(),
});

type SelfManagedFormData = z.infer<typeof selfManagedFormSchema>;
type ManagedFormData = z.infer<typeof managedFormSchema>;

interface UpdateDatabaseConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  databaseConnection: DatabaseConnection;
}

const databaseEngineOptions = [
  { value: 'SQL_SERVER', label: 'SQL Server' },
  { value: 'MYSQL', label: 'MySQL' },
  { value: 'POSTGRESQL', label: 'PostgreSQL' },
  { value: 'ORACLE', label: 'Oracle' },
];

const getEngineDisplayName = (engine: string): string => {
  switch (engine) {
    case 'SQL_SERVER': return 'SQL Server';
    case 'MYSQL': return 'MySQL';
    case 'POSTGRESQL': return 'PostgreSQL';
    case 'ORACLE': return 'Oracle';
    default: return engine;
  }
};

/**
 * UpdateDatabaseConnectionModal component
 */
export const UpdateDatabaseConnectionModal: React.FC<UpdateDatabaseConnectionModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  databaseConnection,
}) => {
  const isManaged = databaseConnection.connectionType === 'KETRICS_MANAGED';

  return isManaged ? (
    <ManagedUpdateForm
      isOpen={isOpen}
      onClose={onClose}
      tenantId={tenantId}
      databaseConnection={databaseConnection}
    />
  ) : (
    <SelfManagedUpdateForm
      isOpen={isOpen}
      onClose={onClose}
      tenantId={tenantId}
      databaseConnection={databaseConnection}
    />
  );
};

/**
 * Self-managed connection update form
 */
const SelfManagedUpdateForm: React.FC<UpdateDatabaseConnectionModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  databaseConnection,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<SelfManagedFormData>({
    resolver: zodResolver(selfManagedFormSchema),
    defaultValues: {
      dbConnectionName: databaseConnection.dbConnectionName,
      description: databaseConnection.description || '',
      engine: databaseConnection.engine,
      host: databaseConnection.host || '',
      port: databaseConnection.port?.toString() || '5432',
      database: databaseConnection.database || '',
      username: databaseConnection.username || '',
      password: '',
      ssl: databaseConnection.ssl ?? true,
      isActive: databaseConnection.isActive,
      portForwarding: databaseConnection.portForwarding ?? false,
      sshHost: databaseConnection.sshHost || '',
      sshPort: databaseConnection.sshPort?.toString() || '22',
      sshUsername: databaseConnection.sshUsername || '',
    },
  });

  const watchPortForwarding = watch('portForwarding');

  // Reset form when database connection changes
  useEffect(() => {
    reset({
      dbConnectionName: databaseConnection.dbConnectionName,
      description: databaseConnection.description || '',
      engine: databaseConnection.engine,
      host: databaseConnection.host || '',
      port: databaseConnection.port?.toString() || '5432',
      database: databaseConnection.database || '',
      username: databaseConnection.username || '',
      password: '',
      ssl: databaseConnection.ssl ?? true,
      isActive: databaseConnection.isActive,
      portForwarding: databaseConnection.portForwarding ?? false,
      sshHost: databaseConnection.sshHost || '',
      sshPort: databaseConnection.sshPort?.toString() || '22',
      sshUsername: databaseConnection.sshUsername || '',
    });
  }, [databaseConnection, reset]);

  const { mutate: updateDatabaseConnection, isPending } = useUpdateDatabaseConnection(
    tenantId,
    databaseConnection.dbConnectionId,
    {
      onSuccess: () => {
        setSuccessMessage('Database connection updated successfully!');
        setErrorMessage('');
        setTimeout(() => {
          onClose();
          setSuccessMessage('');
        }, 1500);
      },
      onError: (error) => {
        setErrorMessage(error);
        setSuccessMessage('');
      },
    }
  );

  const onSubmit = (data: SelfManagedFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    const connectionData = {
      dbConnectionName: data.dbConnectionName,
      description: data.description || undefined,
      engine: data.engine as DatabaseEngine,
      host: data.host,
      port: parseInt(data.port),
      database: data.database,
      username: data.username,
      password: data.password || undefined, // Only send if provided
      ssl: data.ssl,
      isActive: data.isActive,
      portForwarding: data.portForwarding ?? false,
      sshHost: data.portForwarding && data.sshHost ? data.sshHost : null,
      sshPort: data.portForwarding && data.sshPort ? parseInt(data.sshPort) : null,
      sshUsername: data.portForwarding && data.sshUsername ? data.sshUsername : null,
    };

    updateDatabaseConnection(connectionData);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      setShowPassword(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>Edit Database Connection</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Connection Code (read-only) */}
          <div className="bg-gray-50 p-3 rounded-md flex items-center gap-3">
            <p className="text-sm text-gray-600">
              Connection Code: <span className="font-mono font-medium">{databaseConnection.dbConnectionCode}</span>
              <span className="ml-2 text-xs text-gray-500">(immutable)</span>
            </p>
            <Badge variant="warning">Self-Managed</Badge>
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Connection Name */}
              <FormField label="Connection Name" required error={errors.dbConnectionName?.message}>
                <Input
                  {...register('dbConnectionName')}
                  placeholder="Production Database"
                  error={!!errors.dbConnectionName}
                />
              </FormField>

              {/* Database Engine */}
              <FormField label="Database Engine" required error={errors.engine?.message}>
                <Select {...register('engine')} error={!!errors.engine}>
                  {databaseEngineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>

            {/* Description */}
            <div className="mt-4">
              <FormField label="Description" error={errors.description?.message}>
                <Textarea
                  {...register('description')}
                  placeholder="Describe the purpose of this database connection..."
                  rows={2}
                  error={!!errors.description}
                />
              </FormField>
            </div>
          </div>

          {/* Connection Details */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Connection Details</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Host */}
              <FormField label="Host" required error={errors.host?.message}>
                <Input
                  {...register('host')}
                  placeholder="localhost or db.example.com"
                  error={!!errors.host}
                />
              </FormField>

              {/* Port */}
              <FormField label="Port" required error={errors.port?.message}>
                <Input
                  {...register('port')}
                  type="number"
                  min="1"
                  max="65535"
                  error={!!errors.port}
                />
              </FormField>

              {/* Database Name */}
              <FormField label="Database Name" required error={errors.database?.message}>
                <Input
                  {...register('database')}
                  placeholder="mydb"
                  error={!!errors.database}
                />
              </FormField>

              {/* Username */}
              <FormField label="Username" required error={errors.username?.message}>
                <Input
                  {...register('username')}
                  placeholder="db_user"
                  error={!!errors.username}
                />
              </FormField>

              {/* Password */}
              <FormField label="Password" error={errors.password?.message}>
                <div className="relative">
                  <Input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Leave empty to keep current password"
                    error={!!errors.password}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to keep the current password.
                </p>
              </FormField>
            </div>

            {/* SSL Connection */}
            <div className="mt-4 flex items-center">
              <Checkbox
                id="ssl"
                {...register('ssl')}
              />
              <label htmlFor="ssl" className="ml-2 text-sm text-gray-900">
                Use SSL/TLS connection
              </label>
              <span className="ml-2 text-xs text-gray-500">(recommended for production)</span>
            </div>
          </div>

          {/* SSH Port Forwarding */}
          <div>
            <div className="flex items-center mb-4">
              <Checkbox
                id="portForwarding"
                {...register('portForwarding')}
              />
              <label htmlFor="portForwarding" className="ml-2 text-sm font-medium text-gray-900">
                Enable SSH Port Forwarding
              </label>
            </div>

            {watchPortForwarding && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 pl-6 border-l-2 border-gray-200">
                {/* SSH Host */}
                <FormField label="SSH Host" error={errors.sshHost?.message}>
                  <Input
                    {...register('sshHost')}
                    placeholder="bastion.example.com"
                    error={!!errors.sshHost}
                  />
                </FormField>

                {/* SSH Port */}
                <FormField label="SSH Port" error={errors.sshPort?.message}>
                  <Input
                    {...register('sshPort')}
                    type="number"
                    min="1"
                    max="65535"
                    error={!!errors.sshPort}
                  />
                </FormField>

                {/* SSH Username */}
                <FormField label="SSH Username" error={errors.sshUsername?.message}>
                  <Input
                    {...register('sshUsername')}
                    placeholder="ssh_user"
                    error={!!errors.sshUsername}
                  />
                </FormField>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center">
            <Checkbox
              id="isActive"
              {...register('isActive')}
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-900">
              Active (connection is available for use)
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Update Connection
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

/**
 * Ketrics-managed connection update form (simplified)
 */
const ManagedUpdateForm: React.FC<UpdateDatabaseConnectionModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  databaseConnection,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ManagedFormData>({
    resolver: zodResolver(managedFormSchema),
    defaultValues: {
      dbConnectionName: databaseConnection.dbConnectionName,
      description: databaseConnection.description || '',
      isActive: databaseConnection.isActive,
    },
  });

  // Reset form when database connection changes
  useEffect(() => {
    reset({
      dbConnectionName: databaseConnection.dbConnectionName,
      description: databaseConnection.description || '',
      isActive: databaseConnection.isActive,
    });
  }, [databaseConnection, reset]);

  const { mutate: updateDatabaseConnection, isPending } = useUpdateDatabaseConnection(
    tenantId,
    databaseConnection.dbConnectionId,
    {
      onSuccess: () => {
        setSuccessMessage('Database connection updated successfully!');
        setErrorMessage('');
        setTimeout(() => {
          onClose();
          setSuccessMessage('');
        }, 1500);
      },
      onError: (error) => {
        setErrorMessage(error);
        setSuccessMessage('');
      },
    }
  );

  const onSubmit = (data: ManagedFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    updateDatabaseConnection({
      dbConnectionName: data.dbConnectionName,
      description: data.description || undefined,
      isActive: data.isActive,
    });
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>Edit Database Connection</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Connection Code and Type (read-only) */}
          <div className="bg-gray-50 p-3 rounded-md flex items-center gap-3">
            <p className="text-sm text-gray-600">
              Connection Code: <span className="font-mono font-medium">{databaseConnection.dbConnectionCode}</span>
              <span className="ml-2 text-xs text-gray-500">(immutable)</span>
            </p>
            <Badge variant="info">Ketrics-Managed</Badge>
          </div>

          {/* Read-only connection info */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Connection Details</h3>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-gray-500">Database Engine</dt>
                <dd className="mt-0.5 text-sm text-gray-900">{getEngineDisplayName(databaseConnection.engine)}</dd>
              </div>
              {databaseConnection.managedSqlDbCode && (
                <div>
                  <dt className="text-xs font-medium text-gray-500">Managed Database</dt>
                  <dd className="mt-0.5 text-sm text-gray-900 font-mono">{databaseConnection.managedSqlDbCode}</dd>
                </div>
              )}
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-gray-500">Authentication</dt>
                <dd className="mt-0.5 text-sm text-gray-500 italic">IAM-based authentication (managed by platform)</dd>
              </div>
            </dl>
          </div>

          {/* Editable fields */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4">
              {/* Connection Name */}
              <FormField label="Connection Name" required error={errors.dbConnectionName?.message}>
                <Input
                  {...register('dbConnectionName')}
                  placeholder="Production Database"
                  error={!!errors.dbConnectionName}
                />
              </FormField>

              {/* Description */}
              <FormField label="Description" error={errors.description?.message}>
                <Textarea
                  {...register('description')}
                  placeholder="Describe the purpose of this database connection..."
                  rows={2}
                  error={!!errors.description}
                />
              </FormField>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center">
            <Checkbox
              id="isActive"
              {...register('isActive')}
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-900">
              Active (connection is available for use)
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Update Connection
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
